'use client';

import { useTextField } from '@react-aria/textfield';
import { mergeRefs } from '@react-aria/utils';
import {
  type FocusEvent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormDescription } from '../../internal/components/form/FormDescription';
import { FormField } from '../../internal/components/form/FormField';
import { FormFieldSlotContainer } from '../../internal/components/form/FormFieldSlotContext';
import { FormLabel } from '../../internal/components/form/FormLabel';
import { useControlledState } from '../../internal/hooks/useControlledState/useControlledState';
import { useEffectOnChange } from '../../internal/hooks/useEffectOnChange/useEffectOnChange';
import { filterProps } from '../../internal/utils/filterProps';
import { type Component, type ComponentProps } from '../../types/component';
import { type ResponsiveOption } from '../../types/responsiveOption';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import {
  TextFieldInternalContext,
  type TextFieldType,
} from './TextFieldContext';

const textFieldClasses = {
  root: 'zds-text-field-root',
  input: 'zds-text-field-input',
} as const;

const textFieldStyles = tv({
  slots: {
    root: [textFieldClasses.root, 'flex flex-col'],
    input: [
      textFieldClasses.input,
      // Reset default browser input styles
      'm-0 appearance-none border-0 p-0',
      'min-w-0 flex-1 bg-transparent outline-none',
      // Hide native clear/decoration buttons (WebKit search, IE/Edge)
      '[&::-webkit-search-cancel-button]:appearance-none',
      '[&::-webkit-search-decoration]:appearance-none',
      'h-full w-full pt-0.5 text-md text-primary',
      'truncate placeholder:text-secondary',
    ],
  },
  variants: {
    isDisabled: {
      true: {
        input: 'disabled-foreground placeholder:disabled-foreground',
      },
    },
  },
});

interface TextFieldSlotProps {
  /** Additional props for the `<input>` element. */
  input?: ComponentProps<object, 'input'>;
}

interface TextFieldBaseProps {
  /** The label content displayed above the input. When omitted, provide `aria-labelledby` for accessibility. */
  label?: ReactNode;

  /** Secondary content displayed after the label (e.g., "Optional"). Requires `label` to be set. */
  subLabel?: ReactNode;

  /** Content rendered at the end of the label row (e.g., character count "1/20"). Requires `label` to be set. */
  endLabel?: ReactNode;

  /**
   * Default helper text displayed below the input. Shows in error/success style when
   * `isInvalid`/`isSuccess` is set and no `errorMessage`/`successMessage` is provided.
   */
  description?: string;

  /** Error message shown when `isInvalid` is true. Overrides `description` when present. */
  errorMessage?: string;

  /** Success message shown when `isSuccess` is true. Overrides `description` when present. */
  successMessage?: string;

  /** The size of the text field. Supports responsive breakpoints. @default 'md' */
  size?: ResponsiveOption<'md' | 'lg'>;

  /** Content rendered in the start slot (before input text). Use `TextField.Slot` or action components. */
  startSlot?: ReactNode;

  /** Content rendered in the end slot (after input text). Use `TextField.Slot` or action components. */
  endSlot?: ReactNode;

  /** Whether the field is in an invalid/error state. @default false */
  isInvalid?: boolean;

  /** Whether the field is in a success state. @default false */
  isSuccess?: boolean;

  /** Whether the field is disabled. @default false */
  isDisabled?: boolean;

  /** Whether the field is required. Shows a critical mark (asterisk) on the label. @default false */
  isRequired?: boolean;

  /** The current value (controlled). */
  value?: string;

  /** The default value (uncontrolled). */
  defaultValue?: string;

  /** Handler called when the value changes. Receives the new string value. */
  onChange?: (value: string) => void;

  /** Handler called when the input receives focus. */
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;

  /** Handler called when the input loses focus. */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;

  /** The type of the input element. @default 'text' */
  type?: TextFieldType;

  /** Placeholder text shown when the input is empty. */
  placeholder?: string;

  /** Maximum number of characters allowed. */
  maxLength?: number;

  /** Hint for the virtual keyboard on mobile. When omitted, the browser infers from `type`. */
  inputMode?:
    | 'none'
    | 'text'
    | 'numeric'
    | 'decimal'
    | 'url'
    | 'email'
    | 'search';

  /** Autocomplete hint for the browser. */
  autoComplete?: string;

  /** Whether the input should receive focus on mount. @default false */
  autoFocus?: boolean;

  /** The name attribute for form submission. */
  name?: string;

  /**
   * Props for internal sub-elements.
   * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-textfield-components-textfield--overview#slotprops | slotProps documentation}
   */
  slotProps?: TextFieldSlotProps;
}

type TextFieldProps = ComponentProps<TextFieldBaseProps, 'div'>;

type TextFieldComponent = Component<TextFieldBaseProps, 'div'>;

const TextFieldRoot: TextFieldComponent = (props) => {
  const [localProps, textFieldRaProps] = filterProps(props, ['useTextField']);

  const {
    subLabel,
    endLabel,
    successMessage,
    size = 'md',
    startSlot,
    endSlot,
    isSuccess,
    slotProps,
    className,
    ref,
    ...restLocalProps
  } = localProps;

  const {
    label,
    description,
    errorMessage,
    isInvalid,
    isDisabled,
    isRequired,
    value: valueProp,
    defaultValue,
    onChange: onChangeProp,
    type: typeProp = 'text',
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  // useTextField manages its own controlled state internally but doesn't expose
  // setValue — only a DOM event handler. We need setValue for sub-components like
  // ActionClear that programmatically update the value via context.
  const [value, setValue] = useControlledState(
    valueProp,
    defaultValue ?? '',
    onChangeProp
  );
  const [type, setType] = useState<TextFieldType>(typeProp);

  // Sync internal type state when the prop changes (e.g. parent switches type dynamically)
  useEffectOnChange(() => setType(typeProp), [typeProp]);

  const { labelProps, inputProps, descriptionProps, errorMessageProps } =
    useTextField(
      {
        ...textFieldRaProps,
        errorMessage: isInvalid ? errorMessage : undefined,
        value,
        onChange: setValue,
        type,
      },
      inputRef
    );

  const contextValue = useMemo(
    () => ({
      inputId: inputProps.id,
      inputRef,
      value,
      setValue,
      type,
      setType,
      isDisabled,
    }),
    [inputProps.id, inputRef, value, setValue, type, setType, isDisabled]
  );

  const styles = textFieldStyles({ isDisabled });

  // Select the correct aria props for the description element
  const descAriaProps =
    isInvalid && errorMessage ? errorMessageProps : descriptionProps;

  return (
    <TextFieldInternalContext.Provider value={contextValue}>
      <div
        {...restLocalProps}
        ref={ref}
        className={styles.root({ className })}
        data-disabled={isDisabled || undefined}
        data-invalid={isInvalid || undefined}
      >
        <FormLabel
          label={label}
          subLabel={subLabel}
          isRequired={isRequired}
          isDisabled={isDisabled}
          endLabel={endLabel}
          slotProps={{ label: { ...labelProps } }}
        />

        <FormField
          size={size}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          render={(focusProps) => (
            <>
              <FormFieldSlotContainer position="start" isDisabled={isDisabled}>
                {startSlot}
              </FormFieldSlotContainer>
              <input
                {...mergeProps(inputProps, focusProps, slotProps?.input ?? {})}
                ref={mergeRefs(inputRef, slotProps?.input?.ref)}
                className={styles.input({
                  className: slotProps?.input?.className,
                })}
              />
              <FormFieldSlotContainer position="end" isDisabled={isDisabled}>
                {endSlot}
              </FormFieldSlotContainer>
            </>
          )}
        />

        <FormDescription
          description={description}
          errorMessage={errorMessage}
          successMessage={successMessage}
          isInvalid={isInvalid}
          isSuccess={isSuccess}
          isDisabled={isDisabled}
          {...descAriaProps}
        />
      </div>
    </TextFieldInternalContext.Provider>
  );
};

TextFieldRoot.displayName = 'TextField';

export {
  TextFieldRoot,
  type TextFieldProps,
  type TextFieldBaseProps,
  textFieldClasses,
};
